// src/components/facilityEvents/EventList.jsx
//
// The facility's events (ShowFacility.jsx), apart from their creation: a
// « Nouvel événement » button (EventFormDialog.jsx), then the events grouped
// - En cours, À venir, and Passés et désactivés folded away - each as a card
// with its actions. Grouping and dates: eventStatus.js.
import React, { useMemo, useState } from 'react';
import {
  Box, Paper, Stack, Typography, Button, Chip, IconButton, Tooltip, Collapse, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { groupEvents, eventStatus, formatRange, STATUS } from './eventStatus';

function EventCard({ ev, eventTypes, onRedo, onEdit, onArchive, onUnarchive, onDelete }) {
  const { t, i18n } = useTranslation();
  const now = new Date();
  const status = eventStatus(ev, now);
  const inactive = status === STATUS.INACTIVE;
  const type = eventTypes.find((et) => et.value === Number(ev.type));
  return (
    <Paper variant="outlined" sx={{ p: 2, opacity: inactive ? 0.65 : 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
        <Chip size="small" label={type?.label ?? ev.type}
          sx={{ bgcolor: type?.color ?? '#475569', color: '#fff', fontWeight: 600 }} />
        {inactive && <Chip size="small" variant="outlined" label={t('showFacility.inactive')} />}
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto !important' }}>
          {formatRange(ev, i18n.language, now, { today: t('showFacility.today'), tomorrow: t('showFacility.tomorrow') })}
        </Typography>
      </Stack>
      <Typography variant="h6" sx={{ lineHeight: 1.3 }}>{ev.name}</Typography>
      {ev.message && <Typography color="text.secondary">{ev.message}</Typography>}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
        <Button size="small" variant="outlined" onClick={() => onRedo(ev)}>{t('showFacility.redoToday')}</Button>
        {inactive ? (
          <Button size="small" variant="contained" onClick={() => onUnarchive(ev.id)}>{t('showFacility.activate')}</Button>
        ) : (
          <Button size="small" onClick={() => onArchive(ev.id)}>{t('showFacility.deactivate')}</Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title={t('showFacility.edit')}>
          <IconButton aria-label={t('showFacility.edit')} onClick={() => onEdit(ev)}><EditIcon /></IconButton>
        </Tooltip>
        <Tooltip title={t('showFacility.delete')}>
          <IconButton aria-label={t('showFacility.delete')} onClick={() => onDelete(ev.id)}><DeleteIcon /></IconButton>
        </Tooltip>
      </Stack>
    </Paper>
  );
}

function Group({ title, events, children }) {
  if (!events.length) return null;
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {title} ({events.length})
      </Typography>
      <Stack spacing={1.5}>{children}</Stack>
    </Box>
  );
}

export default function EventList({ events, loading, eventTypes, onNew, ...actions }) {
  const { t } = useTranslation();
  const [showPast, setShowPast] = useState(false);
  const groups = useMemo(() => groupEvents(events), [events]);
  const card = (ev) => <EventCard key={ev.id} ev={ev} eventTypes={eventTypes} {...actions} />;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h6">{t('showFacility.myEvents')}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={onNew}>{t('showFacility.newEvent')}</Button>
      </Stack>

      {loading && <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={28} /></Box>}

      {!loading && events.length === 0 && (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>{t('showFacility.noEventsYet')}</Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={onNew}>{t('showFacility.newEvent')}</Button>
        </Paper>
      )}

      {!loading && (
        <>
          <Group title={t('showFacility.groupCurrent')} events={groups.current}>{groups.current.map(card)}</Group>
          <Group title={t('showFacility.groupUpcoming')} events={groups.upcoming}>{groups.upcoming.map(card)}</Group>
          {groups.past.length > 0 && (
            <Box>
              <Button size="small" color="inherit" onClick={() => setShowPast((v) => !v)}
                endIcon={<ExpandMoreIcon sx={{ transform: showPast ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />}>
                {t('showFacility.groupPast')} ({groups.past.length})
              </Button>
              <Collapse in={showPast} unmountOnExit>
                <Stack spacing={1.5} sx={{ mt: 1 }}>{groups.past.map(card)}</Stack>
              </Collapse>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
